import { useCallback, useMemo, useState } from "react"
import { RefreshCw } from "lucide-react"
import type {
  StudioOntologyOperatingModelAxis,
  StudioPublishReadinessGate,
} from "@/services/project-api"
import { useStudioStore } from "@/store/studio-store"
import { AskCogoSurfaceButton } from "./ask-cogo-surface-button"
import {
  humanizePublishReason,
  type HumanizedPublishReason,
} from "./publish-gate-copy"
import { useStudioLocale } from "@/i18n"
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales"

/**
 * Shared publish/deploy gate diagnostics (F9, Lane 5).
 *
 * Both `publish-surface.tsx` and `deploy-surface.tsx` rendered byte-near-
 * identical raw `key=value` mono dumps of the readiness evaluator's status
 * fields ("speed=pass; quality=pass; …", "requested=web; ready=web; …") to
 * EVERY persona, plus a single engineering-jargon reason line — with no path
 * forward for a creator. This component is the one shared render path:
 *
 *  - CREATORS (default persona) get the gate's blocking reasons humanized into
 *    plain-language titles + details + a resolution action (re-run the readiness
 *    check, or ask Cogo to help). All reasons are shown, de-duped — no more
 *    truncation to `reasons[0]`.
 *  - DEVELOPERS additionally get the full raw `key=value` diagnostics in a
 *    collapsed `<details>` (the F29/F30 demotion idiom) so the engineering depth
 *    stays available where it's wanted.
 *
 * The same component hosts both surfaces, so the humanized copy and the
 * resolution affordance never drift between Publish and Deploy.
 */
export function PublishGateRows({
  publishGate,
}: {
  publishGate: StudioPublishReadinessGate
}) {
  const { locale } = useStudioLocale()
  const developer = useStudioStore((s) => s.studioPersona === "developer")
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const refreshPublishEvidence = useStudioStore((s) => s.refreshPublishEvidence)
  const [rechecking, setRechecking] = useState(false)

  const blocked = !publishGate.publishReady
  const operatingModelGate = publishGate.ontologyOperatingModelGate
  const showOperatingModelGate = shouldShowOperatingModelGate(operatingModelGate)

  // De-dupe exact reasons while preserving every distinct blocker. The former
  // title-level de-dupe made the default list shorter, but it also erased
  // technical distinctions. Progressive disclosure now carries the weight:
  // one primary item stays visible and every distinct blocker remains in the
  // collapsed technical details.
  const humanized = useMemo<HumanizedPublishReason[]>(() => {
    const seenReasons = new Set<string>()
    const out: HumanizedPublishReason[] = []
    for (const reason of [
      ...publishGate.reasons,
      ...operatingModelGate.reasons,
    ]) {
      if (seenReasons.has(reason)) continue
      seenReasons.add(reason)
      out.push(humanizePublishReason(reason, locale))
    }
    return out
  }, [publishGate.reasons, operatingModelGate.reasons, locale])

  // Per-gate-class affordances: only offer a button a reason ACTUALLY supports.
  // A 'recheck' is shown only when some blocking reason can be unblocked by
  // re-reading persisted evidence; 'ask-cogo' only when some reason is a
  // content defect chat can fix. Proof/coverage/sandbox gates carry neither —
  // they are informational, so neither button appears for them (ST-2/ST-3).
  const recheckReason = useMemo(
    () => humanized.find((h) => h.action?.kind === "recheck"),
    [humanized],
  )
  const canRecheck = Boolean(recheckReason)
  const askCogoReason = useMemo(
    () => humanized.find((h) => h.action?.kind === "ask-cogo"),
    [humanized],
  )
  const primaryReason = askCogoReason ?? recheckReason ?? humanized[0] ?? null
  const copy = PUBLISH_GATE_UI_COPY[locale]
  const askCogoContext = useMemo(
    () =>
      [
        copy.blockedContext,
        ...humanized.map((reason, index) =>
          `${index + 1}. ${reason.title}${reason.detail ? ` — ${reason.detail}` : ""}`,
        ),
        showOperatingModelGate
          ? `${copy.sourceModel}: ${operatingModelGate.status}; ${copy.checkRefs(
              operatingModelGate.evidenceRefs.length,
            )}`
          : null,
      ]
        .filter(Boolean)
        .join("\n"),
    [copy, humanized, operatingModelGate, showOperatingModelGate],
  )

  const handleRecheck = useCallback(async () => {
    if (!currentProjectId || rechecking) return
    setRechecking(true)
    try {
      // NON-DESTRUCTIVE re-read: refreshPublishEvidence copies ONLY the latest
      // persisted readiness-evidence fields onto currentProject — it does NOT
      // wholesale-reload the project, so unsaved edits, isDirty, and a pending
      // saveConflict survive (CA-2). The gate is derived from currentProject
      // every render, so refreshing the evidence re-evaluates it against server
      // truth. This re-reads — it never runs an audit/packaging step itself.
      await refreshPublishEvidence()
    } finally {
      setRechecking(false)
    }
  }, [currentProjectId, refreshPublishEvidence, rechecking])

  return (
    <>
      <Row label={copy.readiness}>
        <div className="max-w-[360px] text-right">
          <span
            className={`text-xs font-semibold ${
              publishGate.publishReady ? "text-foreground" : "text-destructive"
            }`}
          >
            {publishGate.publishReady ? copy.ready : copy.blocked}
          </span>
          {blocked && primaryReason && (
            <div className="mt-2 text-left">
              <p className="text-xs font-medium text-foreground">
                {primaryReason.title}
              </p>
              {primaryReason.detail && (
                <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                  {primaryReason.detail}
                </p>
              )}
            </div>
          )}
          {blocked && (canRecheck || askCogoReason) && (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              {canRecheck && (
                <button
                  type="button"
                  onClick={handleRecheck}
                  disabled={!currentProjectId || rechecking}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${rechecking ? "animate-spin" : ""}`}
                    aria-hidden
                  />
                  {rechecking
                    ? locale === "ko"
                      ? "다시 확인 중…"
                      : "Re-checking…"
                    : recheckReason?.action?.label}
                </button>
              )}
              {askCogoReason && (
                <AskCogoSurfaceButton
                  label={askCogoReason.action?.label}
                  context={askCogoContext}
                  chatContextPatch={{ surfaceId: "publish" }}
                  operationKind="publish-readiness"
                  className="h-7 text-[11px]"
                  keepVisibleWhenChatOpen
                />
              )}
            </div>
          )}
        </div>
      </Row>

      {blocked && (humanized.length > 0 || showOperatingModelGate || developer) && (
        <Row label={copy.technicalDetailsLabel}>
          <details
            data-testid="publish-gate-technical-details"
            className="max-w-[360px] text-right"
          >
            <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground">
              {copy.technicalDetails(humanized.length)}
            </summary>
            <div className="mt-3 space-y-4 text-left">
              {humanized.length > 0 && (
                <section>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                    {copy.blockerList}
                  </p>
                  <ol className="mt-2 space-y-2">
                    {humanized.map((reason, index) => (
                      <li key={`${reason.title}:${index}`} className="text-left">
                        <p className="text-xs font-medium text-foreground">
                          {reason.title}
                        </p>
                        {reason.detail && (
                          <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                            {reason.detail}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {showOperatingModelGate && (
                <section>
                  <div className="flex flex-wrap items-center justify-between gap-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                      {copy.sourceModel}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <IntegrationStatusPill status={operatingModelGate.status} />
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {copy.checkRefs(operatingModelGate.evidenceRefs.length)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5 text-left">
                    {ONTOLOGY_AXIS_ORDER.map((axis) => (
                      <div
                        key={axis}
                        className="flex min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1"
                      >
                        <span className="truncate text-[11px] text-muted-foreground">
                          {formatOntologyAxisLabel(axis)}
                        </span>
                        <IntegrationStatusPill
                          status={operatingModelGate.axisStatuses[axis]}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {developer && (
                <section className="space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                    {copy.developerDiagnostics}
                  </p>
              <DumpLine label="readiness">
                ok={String(publishGate.ok)}; mode={publishGate.packagingMode};
                security={publishGate.securityProfile}
              </DumpLine>
              <DumpLine label="target matrix" status={publishGate.targetMatrixGate.status}>
                requested=
                {publishGate.targetMatrixGate.requestedTargets.join(",") || "none"}; ready=
                {publishGate.targetMatrixGate.readyTargets.join(",") || "none"}; partial=
                {publishGate.targetMatrixGate.partialTargets.join(",") || "none"}; missing=
                {publishGate.targetMatrixGate.missingTargets.join(",") || "none"}; axisGaps=
                {Object.entries(publishGate.targetMatrixGate.nativeAxisGaps)
                  .map(([target, gaps]) => `${target}:${gaps.join("+")}`)
                  .join(";") || "none"}
              </DumpLine>
              <DumpLine label="actionflow contract" status={publishGate.actionFlowReadinessGate.status}>
                required={String(publishGate.actionFlowReadinessGate.required)}; version=
                {publishGate.actionFlowReadinessGate.contractVersion ?? "none"}; evidence=
                {publishGate.actionFlowReadinessGate.evidenceRefs.length}; reasons=
                {publishGate.actionFlowReadinessGate.reasons.length}
              </DumpLine>
              <DumpLine label="report/export contract" status={publishGate.reportExportReadinessGate.status}>
                required={String(publishGate.reportExportReadinessGate.required)}; version=
                {publishGate.reportExportReadinessGate.contractVersion ?? "none"}; evidence=
                {publishGate.reportExportReadinessGate.evidenceRefs.length}; reasons=
                {publishGate.reportExportReadinessGate.reasons.length}
              </DumpLine>
              <DumpLine label="ontology matching" status={publishGate.ontologyMatchingReadinessGate.status}>
                required={String(publishGate.ontologyMatchingReadinessGate.required)}; decisions=
                {publishGate.ontologyMatchingReadinessGate.decisionCount}; blocked=
                {publishGate.ontologyMatchingReadinessGate.blockedDecisionCount}; candidates=
                {publishGate.ontologyMatchingReadinessGate.candidateDecisionCount}; actionFlowValidation=
                {String(publishGate.ontologyMatchingReadinessGate.actionFlowValidationRequired)}; evidence=
                {publishGate.ontologyMatchingReadinessGate.evidenceRefs.length}; requiredValidation=
                {publishGate.ontologyMatchingReadinessGate.requiredValidation.join("+") || "none"}; compiler=
                {publishGate.ontologyMatchingReadinessGate.compilerContractStatus}; entrypoints=
                {publishGate.ontologyMatchingReadinessGate.enforcedEntrypoints.join("+") || "none"}
              </DumpLine>
              <DumpLine label="ontology operating model" status={operatingModelGate.status}>
                required={String(operatingModelGate.required)}; axes=
                {ONTOLOGY_AXIS_ORDER.map((axis) => `${axis}:${operatingModelGate.axisStatuses[axis]}`).join("+")};
                evidence={operatingModelGate.evidenceRefs.length}; reasons=
                {operatingModelGate.reasons.length}
              </DumpLine>
              <DumpLine label="verification gate" status={publishGate.proofReadinessGate.status}>
                full={publishGate.proofReadinessGate.fullCoverageStatus}; exhaustive=
                {publishGate.proofReadinessGate.exhaustiveProofStatus}; provider=
                {publishGate.proofReadinessGate.providerSlotHydrationProofStatus}; actions=
                {publishGate.proofReadinessGate.runtimeProvenActionCount}/
                {publishGate.proofReadinessGate.runtimeHonoredActionCount}
              </DumpLine>
              <DumpLine label="end-user verification" status={publishGate.endUserProofGate.status}>
                speed={publishGate.endUserProofGate.speedStatus}; quality=
                {publishGate.endUserProofGate.qualityStatus}; style=
                {publishGate.endUserProofGate.designStyleSwitchStatus}; sandbox=
                {publishGate.endUserProofGate.designSandboxStatus}; scenarios=
                {publishGate.endUserProofGate.scenarioMatrixStatus}; adaptive=
                {publishGate.endUserProofGate.adaptiveRuntimeStatus}; state=
                {publishGate.endUserProofGate.stateBoundaryStatus}; data=
                {publishGate.endUserProofGate.datasourceStateProjectionStatus}; axis=
                {publishGate.endUserProofGate.adaptiveAxisCoverageStatus}
              </DumpLine>
              <DumpLine label="data projection">
                passed=
                {publishGate.endUserProofGate.datasourceStateProjectionPassCount}/
                {publishGate.endUserProofGate.datasourceStateProjectionRequiredScenarioCount};
                scenarios=
                {publishGate.endUserProofGate.datasourceStateProjectionScenarioCount}; domains=
                {publishGate.endUserProofGate.datasourceStateProjectionStateDomains.join(",") ||
                  "none"}; datasources=
                {publishGate.endUserProofGate.datasourceStateProjectionDatasourceKinds.join(",") ||
                  "none"}; projections=
                {publishGate.endUserProofGate.datasourceStateProjectionProjectionTextCount}
              </DumpLine>
              <DumpLine label="adaptive axes">
                passed={publishGate.endUserProofGate.adaptiveAxisCoveragePassed}/
                {publishGate.endUserProofGate.adaptiveAxisCoverageRequired}; missing=
                {publishGate.endUserProofGate.adaptiveAxisCoverageMissing.join(",") || "none"}
              </DumpLine>
              <DumpLine
                label="renderpack"
                status={publishGate.renderPackPackagingGate.status}
              >
                {publishGate.renderPackPackagingGate.transport}; blocks=
                {publishGate.renderPackPackagingGate.blockCount}; errors=
                {publishGate.renderPackPackagingGate.errorCount}
              </DumpLine>
              <DumpLine label="adapter gate" status={publishGate.productionAdapterGate.status}>
                actions=
                {publishGate.productionAdapterGate.adapterActions.join(",") || "datasource"};
                missing=
                {publishGate.productionAdapterGate.missingEvidence.join(",") || "none"}
              </DumpLine>
                </section>
              )}
            </div>
          </details>
        </Row>
      )}
    </>
  )
}

interface PublishGateUiCopy {
  readiness: string
  ready: string
  blocked: string
  blockedContext: string
  technicalDetailsLabel: string
  technicalDetails: (count: number) => string
  blockerList: string
  sourceModel: string
  checkRefs: (count: number) => string
  developerDiagnostics: string
}

const PUBLISH_GATE_UI_COPY: Record<LocaleCode, PublishGateUiCopy> = {
  en: {
    readiness: "Publish readiness",
    ready: "Ready to publish",
    blocked: "Needs attention before publishing",
    blockedContext:
      "Publishing is currently blocked. Resolve the following items before publishing.",
    technicalDetailsLabel: "Details",
    technicalDetails: (count) => `View ${count} technical checks`,
    blockerList: "Blocking items",
    sourceModel: "Source model",
    checkRefs: (count) => `${count} check refs`,
    developerDiagnostics: "Developer diagnostics",
  },
  ko: {
    readiness: "발행 준비 상태",
    ready: "발행할 수 있습니다",
    blocked: "발행 전 확인이 필요합니다",
    blockedContext:
      "현재 발행 준비 상태가 차단되어 있습니다. 다음 항목을 해결해야 합니다.",
    technicalDetailsLabel: "상세",
    technicalDetails: (count) => `기술 진단 ${count}개 보기`,
    blockerList: "차단 항목",
    sourceModel: "소스 모델",
    checkRefs: (count) => `검사 근거 ${count}개`,
    developerDiagnostics: "개발자 진단",
  },
  ja: {
    readiness: "公開準備状況",
    ready: "公開できます",
    blocked: "公開前に確認が必要です",
    blockedContext:
      "現在、公開準備がブロックされています。公開前に次の項目を解決してください。",
    technicalDetailsLabel: "詳細",
    technicalDetails: (count) => `技術チェック ${count} 件を表示`,
    blockerList: "ブロック項目",
    sourceModel: "ソースモデル",
    checkRefs: (count) => `チェック根拠 ${count} 件`,
    developerDiagnostics: "開発者診断",
  },
  zh: {
    readiness: "发布准备状态",
    ready: "可以发布",
    blocked: "发布前需要处理",
    blockedContext: "当前发布准备状态受阻。请在发布前解决以下项目。",
    technicalDetailsLabel: "详情",
    technicalDetails: (count) => `查看 ${count} 项技术检查`,
    blockerList: "阻止项目",
    sourceModel: "源模型",
    checkRefs: (count) => `${count} 项检查依据`,
    developerDiagnostics: "开发者诊断",
  },
  "zh-TW": {
    readiness: "發佈準備狀態",
    ready: "可以發佈",
    blocked: "發佈前需要處理",
    blockedContext: "目前發佈準備狀態受阻。請在發佈前解決下列項目。",
    technicalDetailsLabel: "詳細資料",
    technicalDetails: (count) => `檢視 ${count} 項技術檢查`,
    blockerList: "阻擋項目",
    sourceModel: "來源模型",
    checkRefs: (count) => `${count} 項檢查依據`,
    developerDiagnostics: "開發者診斷",
  },
}

const ONTOLOGY_AXIS_ORDER: StudioOntologyOperatingModelAxis[] = [
  "studioGenerationLoop",
  "figmaTemplateQuality",
  "endUserE2E",
  "reportDocumentParity",
  "actionFlowRuntime",
  "ciCostPolicy",
]

const ONTOLOGY_AXIS_LABELS: Record<StudioOntologyOperatingModelAxis, string> = {
  studioGenerationLoop: "Studio generation",
  figmaTemplateQuality: "Design/template",
  endUserE2E: "User E2E",
  reportDocumentParity: "Report/export",
  actionFlowRuntime: "Behavior",
  ciCostPolicy: "CI policy",
}

function shouldShowOperatingModelGate(
  gate: StudioPublishReadinessGate["ontologyOperatingModelGate"],
): boolean {
  return gate.required || gate.evidenceRefs.length > 0 || gate.reasons.length > 0
}

function formatOntologyAxisLabel(axis: StudioOntologyOperatingModelAxis): string {
  return ONTOLOGY_AXIS_LABELS[axis]
}

function IntegrationStatusPill({
  status,
  compact = false,
}: {
  status: string
  compact?: boolean
}) {
  const cls =
    status === "ready"
      ? "border-border bg-muted text-foreground"
      : status === "invalid"
        ? "border-destructive/25 bg-destructive/10 text-destructive"
        : status === "gap" || status === "fallback"
          ? "border-border bg-muted text-muted-foreground"
          : "border-border bg-muted text-muted-foreground"
  return (
    <span
      className={`inline-flex shrink-0 rounded-full border font-mono ${compact ? "px-1.5 py-0 text-[9px]" : "px-2 py-0.5 text-[10px]"} ${cls}`}
    >
      {status}
    </span>
  )
}

function DumpLine({
  label,
  status,
  children,
}: {
  label: string
  status?: string
  children: React.ReactNode
}) {
  const testId = `publish-gate-diagnostics-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`

  return (
    <div data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground/70">
        {label}
        {status ? ` · ${status}` : ""}
      </span>
      <p className="font-mono text-[10px] text-muted-foreground">{children}</p>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between border-b border-border px-5 py-3 last:border-0">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div>{children}</div>
    </div>
  )
}
